import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { useState } from "react";

import { EntityListSearchField } from "~ui/molecules/EntityListSearchField/EntityListSearchField";

import { RenderStates } from "../../atoms/RenderStates/RenderStates";
import { OrganizationItemWithAvatar } from "../../organisms/Organizations/OrganizationItemWithAvatar";
import { type Organization } from "../../organisms/Organizations/types";
import { NavLinkWrapper } from "./NavLinkWrapper";

const tNoSearchMatch = "No organizations match your search.";

export interface AdminOrganizationsTemplateProps {
  data?: Organization[];
  linkTo?: (organization: Organization) => string;
  isSiteAdmin?: boolean;
  showHeader?: boolean;
  status: Utils.QueryStatus;
}

function OrganizationsList({
  isSiteAdmin,
  linkTo,
  organizations,
}: {
  isSiteAdmin: boolean;
  linkTo?: (organization: Organization) => string;
  organizations: Organization[];
}) {
  return (
    <Grid container role="list" spacing={2}>
      {organizations.map((organization) => (
        <Grid key={organization.id} role="listitem" size={12}>
          {linkTo ? (
            <NavLinkWrapper to={linkTo(organization)}>
              <OrganizationItemWithAvatar isSiteAdmin={isSiteAdmin} organization={organization} showType={false} />
            </NavLinkWrapper>
          ) : (
            <OrganizationItemWithAvatar isSiteAdmin={isSiteAdmin} organization={organization} showType={false} />
          )}
        </Grid>
      ))}
    </Grid>
  );
}

export const AdminOrganizationsTemplate = ({
  data: organizations,
  linkTo,
  isSiteAdmin = false,
  showHeader = true,
  status,
}: AdminOrganizationsTemplateProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <Box>
      <Stack spacing={3}>
        {showHeader && <Typography variant="h5">Organizations</Typography>}
        <RenderStates
          data={organizations}
          status={status}
          empty={
            <Box>
              <EntityListSearchField
                id="admin-organizations-search"
                label="Search organizations"
                sx={{ mb: 2 }}
                value={searchQuery}
                onChange={({ target: { value } }) => {
                  setSearchQuery(value);
                }}
              />
              <Typography>No organizations found</Typography>
            </Box>
          }
          error={<Typography color="error">Error loading organizations</Typography>}
          pending={<CircularProgress />}
          success={(orgs) => {
            const q = searchQuery.trim().toLowerCase();
            const list = !q ? orgs : orgs.filter((o) => o.name.toLowerCase().includes(q));
            const noSearchResults = q && list.length === 0;

            if (noSearchResults) {
              return (
                <Box>
                  <EntityListSearchField
                    id="admin-organizations-search"
                    label="Search organizations"
                    value={searchQuery}
                    onChange={({ target: { value } }) => {
                      setSearchQuery(value);
                    }}
                  />
                  <Typography sx={{ mt: 2 }}>{tNoSearchMatch}</Typography>
                </Box>
              );
            }

            return (
              <Box>
                <EntityListSearchField
                  id="admin-organizations-search"
                  label="Search organizations"
                  sx={{ mb: 2 }}
                  value={searchQuery}
                  onChange={({ target: { value } }) => {
                    setSearchQuery(value);
                  }}
                />
                <OrganizationsList isSiteAdmin={isSiteAdmin} linkTo={linkTo} organizations={list} />
              </Box>
            );
          }}
        />
      </Stack>
    </Box>
  );
};
